import React, { useEffect } from 'react';
import { Mic } from 'lucide-react';

interface VoiceInputProps {
  question: { num1: number; num2: number; operator: string; answer: number };
  onNextQuestion: () => void;
  isRecording: boolean;
}

const VoiceInput: React.FC<VoiceInputProps> = ({
  question,
  onNextQuestion,
  isRecording,
}) => {
  const { num1, operator, num2 } = question;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();
        onNextQuestion();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onNextQuestion]);

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Question display — matches TextInput style */}
      <div className="text-6xl font-bold text-gray-800 mb-8">
        {num1} {operator} {num2} = ?
      </div>

      {/* Microphone indicator */}
      <div className="flex flex-col items-center gap-3">
        <div
          className={`flex items-center justify-center w-20 h-20 rounded-full ${
            isRecording
              ? 'bg-indigo-100 ring-4 ring-indigo-300 animate-pulse'
              : 'bg-gray-100 ring-4 ring-gray-200'
          }`}
        >
          <Mic
            className={`w-9 h-9 ${isRecording ? 'text-indigo-600' : 'text-gray-400'}`}
          />
        </div>

        <p className="text-lg text-gray-500">Speak your answer, then press Space</p>
      </div>

      {/* Next button for touch/accessibility */}
      <button
        onClick={onNextQuestion}
        className="px-8 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-base min-h-[48px]"
      >
        Next
      </button>
    </div>
  );
};

export default VoiceInput;
